import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeSwitcher from './ThemeSwitcher';
import { getDownloadUrl, detectPlatform } from '../lib/constants';
import AntivirusModal from './AntivirusModal';

export default function Header() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 768) setMobileOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 移动菜单打开时锁定背景滚动
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navLinks = [
    { key: 'features', href: '#features' },
    { key: 'steps', href: '#steps' },
    { key: 'faq', href: '#faq' },
  ];

  const handleNavClick = () => setMobileOpen(false);

  const handleDownloadClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileOpen(false);
    if (detectPlatform() === 'win_x64') {
      setShowModal(true);
    } else {
      window.location.href = getDownloadUrl();
    }
  };

  const handleConfirm = () => {
    setShowModal(false);
    window.location.href = getDownloadUrl();
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 px-4">
        <nav
          className={`mx-auto mt-4 flex max-w-5xl items-center justify-between rounded-full py-2 pl-5 pr-2 transition-all duration-500 ease-spring ${
            scrolled || mobileOpen
              ? 'border border-[#1414131a] bg-[#faf9f5]/80 shadow-warm backdrop-blur-xl dark:border-[#faf9f51a] dark:bg-[#1a1918]/80'
              : 'border border-transparent bg-transparent'
          }`}
        >
          {/* Logo */}
          <a href="#" className="group flex items-center gap-2.5">
            <img src="/app-icon.png" alt="Cherry Agent" className="h-8 w-8 rounded-lg transition-transform duration-300 ease-spring group-hover:scale-105" />
            <span className="text-[15px] font-semibold tracking-tight text-[#141413] dark:text-[#faf9f5]">Cherry Agent</span>
          </a>

          {/* Desktop nav — 居中 */}
          <div className="absolute left-1/2 hidden -translate-x-1/2 items-center md:flex">
            {navLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                className="group relative px-4 py-2 text-sm text-[#6b6a68] transition-colors duration-300 hover:text-[#141413] dark:text-[#9a9893] dark:hover:text-[#faf9f5]"
              >
                {t(`nav.${link.key}`)}
                <span className="absolute -bottom-0.5 left-4 right-4 h-px origin-left scale-x-0 bg-[#ae5630] transition-transform duration-300 ease-spring group-hover:scale-x-100" />
              </a>
            ))}
          </div>

          {/* 右侧 */}
          <div className="flex items-center gap-1.5">
            <ThemeSwitcher />
            <LanguageSwitcher />
            <a
              href="/register"
              className="hidden px-3 py-2 text-sm font-medium text-[#6b6a68] transition-colors duration-300 hover:text-[#141413] dark:text-[#9a9893] dark:hover:text-[#faf9f5] sm:inline-flex"
            >
              {t('nav.register')}
            </a>
            <a
              href={getDownloadUrl()}
              onClick={handleDownloadClick}
              className="group hidden items-center gap-2 rounded-full bg-[#ae5630] py-1.5 pl-4 pr-1.5 text-sm font-medium text-white shadow-warm-sm transition-all duration-300 ease-spring hover:bg-[#c4633a] active:scale-[0.97] sm:inline-flex"
            >
              {t('nav.download')}
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 transition-transform duration-300 ease-spring group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                <ArrowUpRight size={14} />
              </span>
            </a>

            {/* Mobile hamburger — morph 成 X */}
            <button
              onClick={() => setMobileOpen((p) => !p)}
              className="relative flex h-9 w-9 items-center justify-center text-[#141413] dark:text-[#faf9f5] md:hidden"
              aria-label={mobileOpen ? t('a11y.closeMenu') : t('a11y.openMenu')}
              aria-expanded={mobileOpen}
            >
              <span className={`absolute h-[1.5px] w-5 bg-current transition-all duration-300 ease-spring ${mobileOpen ? 'rotate-45' : '-translate-y-[5px]'}`} />
              <span className={`absolute h-[1.5px] w-5 bg-current transition-all duration-300 ease-spring ${mobileOpen ? '-rotate-45' : 'translate-y-[5px]'}`} />
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile 全屏玻璃覆盖层 */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
            className="fixed inset-0 z-40 bg-[#faf9f5]/92 backdrop-blur-2xl dark:bg-[#141413]/92 md:hidden"
          >
            <div className="flex h-full flex-col justify-center gap-1 px-8">
              {navLinks.map((link, i) => (
                <motion.a
                  key={link.key}
                  href={link.href}
                  onClick={handleNavClick}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i + 0.12, duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
                  className="font-display text-5xl font-medium tracking-tight text-[#141413] dark:text-[#faf9f5]"
                >
                  {t(`nav.${link.key}`)}
                </motion.a>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * navLinks.length + 0.12, duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
                className="mt-10 flex flex-col gap-3"
              >
                <a
                  href="/register"
                  onClick={handleNavClick}
                  className="inline-flex items-center justify-center rounded-full border border-[#1414131a] py-3.5 text-base font-medium text-[#141413] transition-colors duration-300 hover:border-[#ae5630] hover:text-[#ae5630] dark:border-[#faf9f51a] dark:text-[#faf9f5]"
                >
                  {t('nav.register')}
                </a>
                <a
                  href={getDownloadUrl()}
                  onClick={handleDownloadClick}
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#ae5630] py-3.5 text-base font-medium text-white shadow-warm transition-all duration-300 ease-spring active:scale-[0.98]"
                >
                  {t('nav.download')}
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15">
                    <ArrowUpRight size={15} />
                  </span>
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AntivirusModal
        open={showModal}
        onConfirm={handleConfirm}
        onClose={() => setShowModal(false)}
      />
    </>
  );
}
